'use client';

import type { ReactNode } from 'react';
import { Children, isValidElement, useMemo } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import type { UiSliderProps } from './UiSlider.types';
import { UiSliderFade } from './UiSliderFade';
import { UiSliderNavigation } from './UiSliderNavigation';
import { useUiSlider } from './useUiSlider';

/** A slide keeps its child's own key, so re-ordering never re-mounts it. */
function keyOf(child: ReactNode, index: number) {
    return isValidElement(child) && child.key != null ? String(child.key) : String(index);
}

/**
 * A horizontal, snap-scrolling track. `.card-track` (slider.css) is what makes
 * it scroll, and it only does so below the grid breakpoint — above it the track
 * is whatever `listClassName` lays out, and the navigation is hidden with it —
 * unless `slideOnDesktop` keeps both. An edge the track runs on past fades out.
 * The slides stay server-rendered: they arrive as `children`.
 */
export function UiSlider({
    children,
    label = 'Slider',
    className,
    listClassName,
    itemClassName,
    slideOnDesktop = false,
}: UiSliderProps) {
    const items = useMemo(() => Children.toArray(children), [children]);
    const { trackRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext, onKeyDown } = useUiSlider(items.length);

    return (
        <div className={cn('card-slider', className)} role="group" aria-roledescription="carousel" aria-label={label}>
            <div className="card-track-viewport">
                <ul
                    ref={trackRef}
                    tabIndex={0}
                    onKeyDown={onKeyDown}
                    className={cn('card-track', slideOnDesktop && 'card-track--desktop', listClassName)}
                >
                    {items.map((child, index) => (
                        <li key={keyOf(child, index)} className={itemClassName}>
                            {child}
                        </li>
                    ))}
                </ul>

                <UiSliderFade side="prev" visible={canScrollPrev} />
                <UiSliderFade side="next" visible={canScrollNext} />
            </div>

            <UiSliderNavigation
                canScrollPrev={canScrollPrev}
                canScrollNext={canScrollNext}
                onPrev={scrollPrev}
                onNext={scrollNext}
                className={slideOnDesktop ? 'card-track-nav--desktop' : undefined}
            />
        </div>
    );
}
